import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
} from "recharts";
import { cn } from "@repo/core";
import { AppCard, KpiCard, KpiIcons, ProgressBar, SectionTitle } from "../components/ui/AppCard";
import { useCurrency } from "../hooks/useCurrency";
import { use503020 } from "../hooks/useBudgetData";
import { monthShortLabel } from "../i18n/dates";
import { groupLabel } from "../i18n/labels";
import { useT } from "../i18n/useT";
import { rule503020Tone, SEMANTIC } from "../lib/semanticColors";
import { motion } from "framer-motion";

interface Props { year: number; person: string }

interface RuleItemProps {
  label: string;
  emoji: string;
  kind: "needs" | "wants" | "savings";
  amount: number;
  actual: number;
  target: number;
  accentClass: string;
}

function RuleItem({ label, emoji, kind, amount, actual, target, accentClass }: RuleItemProps) {
  const { format } = useCurrency();
  const { t } = useT();
  const { tone, labelKey } = rule503020Tone(kind, actual, target);
  const barColor = SEMANTIC[tone].bar;

  return (
    <AppCard padding="none" className="overflow-hidden">
      <div className={cn("px-5 pt-4 pb-3", accentClass)}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-2xl leading-none mb-1">{emoji}</p>
            <p className="font-semibold text-sm text-foreground">{label}</p>
            <p className="text-[10px] text-muted-foreground">{t("rule.targetPct", { n: target })}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-foreground">{actual.toFixed(1)}%</p>
            <p className={cn("text-[11px] font-semibold", SEMANTIC[tone].text)}>{t(labelKey)}</p>
          </div>
        </div>
      </div>
      <div className="px-5 py-3 space-y-1.5">
        <ProgressBar value={actual} showPct={false} height={10} color={barColor} />
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>{t("rule.actual")} {format(amount)}</span>
          <span>0% ← {target}% → 100%</span>
        </div>
      </div>
    </AppCard>
  );
}

function DonutTooltip({ active, payload }: TooltipProps<number, string>) {
  const { format } = useCurrency();
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <div
      className="rounded-xl border border-border bg-card shadow-lg px-3 py-2 text-xs"
      style={{ boxShadow: "var(--card-shadow)" }}
    >
      <p className="font-semibold text-foreground">{item.name}</p>
      <p className="text-primary font-bold mt-0.5">{format(Number(item.value))}</p>
    </div>
  );
}

export function Rule503020Page({ year, person }: Props) {
  const { format } = useCurrency();
  const { t, locale } = useT();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const data = use503020(year, month, person);

  const totalPct    = data.needsPct + data.wantsPct + data.savingsPct;
  const unallocated = 100 - totalPct;

  const pieData = [
    { name: `${groupLabel("needs", t)} (50%)`,   value: data.needs,   pct: data.needsPct,   color: "hsl(258 80% 70%)" },
    { name: `${groupLabel("wants", t)} (30%)`,   value: data.wants,   pct: data.wantsPct,   color: "hsl(32 80% 60%)"  },
    { name: `${groupLabel("savings", t)} (20%)`, value: data.savings, pct: data.savingsPct, color: "hsl(142 60% 50%)" },
  ].filter((d) => d.value > 0);

  return (
    <div className="p-4 lg:p-6 space-y-5 w-full">

      {/* Month selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setMonth((m) => Math.max(1, m - 1))} className="app-btn-outline p-2">
          <ChevronLeft size={15} />
        </button>
        <div className="flex gap-1 flex-wrap">
          {Array.from({ length: 12 }, (_, i) => (
            <button
              key={i}
              onClick={() => setMonth(i + 1)}
              className={cn(
                "px-2.5 py-1 text-xs font-medium rounded-md border transition-all",
                month === i + 1
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground bg-background"
              )}
            >
              {monthShortLabel(i + 1, locale, year)}
            </button>
          ))}
        </div>
        <button onClick={() => setMonth((m) => Math.min(12, m + 1))} className="app-btn-outline p-2">
          <ChevronRight size={15} />
        </button>
      </div>

      {/* Income summary */}
      <KpiCard
        label={t("rule.netIncome")}
        value={format(data.income)}
        color="mint"
        icon={<KpiIcons.moneyIn size={16} />}
        size="lg"
        index={0}
      />

      {/* Donut Chart Visual Breakdown */}
      {pieData.length > 0 && (
        <AppCard padding="md">
          <SectionTitle>{t("rule.distribution")}</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
            <div className="w-full h-44">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={48}
                    outerRadius={68}
                    paddingAngle={4}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke="transparent" />
                    ))}
                  </Pie>
                  <Tooltip content={<DonutTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {pieData.map((item) => (
                <div key={item.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: item.color }} />
                    <span className="font-medium text-foreground">{item.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-foreground">{item.pct.toFixed(1)}%</span>
                    <span className="text-muted-foreground text-[10px] ml-1.5">({format(item.value, true)})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </AppCard>
      )}

      {/* Rule cards */}
      <SectionTitle>{t("rule.breakdown")}</SectionTitle>
      <div className="space-y-3">
        <RuleItem kind="needs" label={`${groupLabel("needs", t)} (50%)`}   emoji="🏠" accentClass="bg-violet-50 dark:bg-violet-950/20" amount={data.needs}   actual={data.needsPct}   target={50} />
        <RuleItem kind="wants" label={`${groupLabel("wants", t)} (30%)`}   emoji="🎯" accentClass="bg-amber-50  dark:bg-amber-950/20"  amount={data.wants}   actual={data.wantsPct}   target={30} />
        <RuleItem kind="savings" label={`${groupLabel("savings", t)} (20%)`} emoji="💰" accentClass="bg-emerald-50 dark:bg-emerald-950/20" amount={data.savings} actual={data.savingsPct} target={20} />
      </div>

      {/* Visual comparison */}
      <AppCard padding="md">
        <SectionTitle>{t("rule.visual")}</SectionTitle>
        <div className="space-y-4">
          {[
            { label: groupLabel("needs", t),   ideal: 50, actual: data.needsPct,   color: "hsl(258 80% 70%)" },
            { label: groupLabel("wants", t),   ideal: 30, actual: data.wantsPct,   color: "hsl(32 80% 60%)"  },
            { label: groupLabel("savings", t), ideal: 20, actual: data.savingsPct, color: "hsl(142 60% 50%)" },
          ].map((row) => (
            <div key={row.label} className="space-y-1">
              <div className="flex justify-between text-[10px] font-medium text-muted-foreground">
                <span>{row.label}</span>
                <span>{row.actual.toFixed(1)}% <span className="text-muted-foreground/50">/ {row.ideal}%</span></span>
              </div>
              {/* Ideal bar (ghost) */}
              <div className="h-2.5 rounded-full overflow-hidden bg-muted">
                <div className="h-full rounded-full opacity-30" style={{ width: `${row.ideal}%`, backgroundColor: row.color }} />
              </div>
              {/* Actual bar */}
              <div className="h-2.5 rounded-full overflow-hidden bg-muted">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, row.actual)}%` }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="h-full rounded-full"
                  style={{ backgroundColor: row.actual > row.ideal + 5 ? "#ef4444" : row.color }}
                />
              </div>
            </div>
          ))}
        </div>
        {unallocated > 0.5 && (
          <p className="text-[11px] text-muted-foreground mt-4 pt-3 border-t border-border">
            ℹ️ {t("rule.unclassified", { n: unallocated.toFixed(1) })}
          </p>
        )}
      </AppCard>
    </div>
  );
}
